import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  getScannedDocument,
  updateScannedDocument,
} from '../../../../../../services/api/scannedDocuments';
import { useIntl } from 'react-intl';
import { concreteTypes } from '../../../../../../constants/general';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import {
  checkDiffTemplate,
  processFieldValue,
  sortRegions,
} from '../../Commons/ScannedDocumentsLayout/utils.index';
import { MainContext } from '../../../../../../context/MainContext';
import LayoutHeader from '../Commons/LayoutHeader';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage';
import { updateProjectTemplate } from '../../../../../../services/api/projectTemplates';
import DeliveryFormFields from '../Commons/DeliveryFormFields';
import {
  deliveryPCFieldGroups,
  deliveryRCFieldGroups,
} from '../Commons/deliveryFieldsGroups';
import {
  listDeliveryNote,
  updateAssayedAndLotifiedScannedDocument,
} from '../../../../../../services/api/deliveryNote';

const UpdateDeliveryScannedDoc = () => {
  const { setLoading, setError } = useContext(MainContext);
  const intl = useIntl();
  const { sdId, model, validated } = useParams();
  const [searchParams] = useSearchParams();
  const { project } = useOutletContext();
  const [initialData, setInitialData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const [savedFormState, setSavedFormState] = useState(null);
  const [savedFileName, setSavedFileName] = useState(null);
  const [template, setLoadedTemplate] = useState(null);
  const noTemplateMsg = intl.formatMessage({ id: 'noTemplate' });
  const noSupplierMsg = intl.formatMessage({ id: 'noSuppliers' });
  const navigate = useNavigate();
  const deliveryFieldGroups = useMemo(() => {
    return model === 'delivery' ? deliveryRCFieldGroups : deliveryPCFieldGroups;
  }, [model]);
  const validableFields = useMemo(
    () =>
      deliveryFieldGroups.mandatory.filter((field) => field !== 'limit_time'),
    [deliveryFieldGroups],
  );
  const isAssayedAndLotified =
    searchParams.get('assayedAndLotified') === 'true';

  const updateDeliveryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      setLoading(true);
      setSavedFormState(formState);
      setSavedFileName(fileName);
      const filteredRegions = regions.filter((r) => r.data !== 'limit_time');
      let scannedDocument;
      if (isAssayedAndLotified) {
        const { data: deliveryNoteResponse } = await listDeliveryNote({
          filters: {
            docket_number: formState.docket_number,
            project: project.id_project,
          },
        });
        const deliveryId = deliveryNoteResponse?.data[0]?.id;
        scannedDocument = await updateAssayedAndLotifiedScannedDocument(
          deliveryId,
          {
            ...formState,
          },
          model,
        );
      } else {
        scannedDocument = await updateScannedDocument(
          sdId,
          {
            delivery_note_data: {
              ...formState,
            },
            regions: template.regions || [],
            fileName,
            camFileName: formState.camFileName,
          },
          model,
        );
      }

      if (scannedDocument.data) {
        setSuccess(true);
        if (
          filteredRegions.every((r) => r.x !== undefined) &&
          checkDiffTemplate(filteredRegions, template.regions)
        ) {
          setDiffTemplate(filteredRegions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    const loadTemplate = async () => {
      try {
        const loadedSD = (await getScannedDocument(sdId, model)).data;
        setLoadedTemplate(loadedSD.template_data || {});
        const loadedDeliveryNote =
          loadedSD.delivery_note_data ||
          loadedSD.no_validated_delivery_note_data;
        const form = {
          templateName: loadedSD.template_data?.name || noTemplateMsg,
          supplier_id: loadedSD.supplier || noSupplierMsg,
          admixtures_extra: processFieldValue(
            'admixtures_extra',
            loadedDeliveryNote.admixtures_extra,
          ),
          detailed_location: processFieldValue(
            'detailed_location',
            loadedDeliveryNote.detailed_location,
          ),
          mash_control: processFieldValue(
            'mash_control',
            loadedDeliveryNote.mash_control,
          ),
          start_discharging_time: processFieldValue(
            'start_discharging_time',
            loadedDeliveryNote.start_discharging_time,
          ),
          finish_discharging_time: processFieldValue(
            'finish_discharging_time',
            loadedDeliveryNote.finish_discharging_time,
          ),
          arriving_time: processFieldValue(
            'arriving_time',
            loadedDeliveryNote.arriving_time,
          ),
          limit_time: processFieldValue(
            'limit_time',
            loadedDeliveryNote.limit_time,
          ),
          elements: processFieldValue('elements', loadedDeliveryNote.elements),
        };
        if (form.mash_control) {
          form.specimens_number = processFieldValue(
            'specimens_number',
            loadedDeliveryNote.specimens_number,
          );
          form.mash_control_extra = processFieldValue(
            'mash_control_extra',
            loadedDeliveryNote.mash_control_extra,
          );
          form.slump = processFieldValue('slump', loadedDeliveryNote.slump);
        }
        setInitialData({
          form: form,
          regions: sortRegions(
            [
              ...deliveryFieldGroups.mandatory.filter(
                (r) => r !== 'limit_time',
              ),
              ...deliveryFieldGroups.other,
            ],
            loadedSD?.regions.length > 0
              ? loadedSD.regions
              : loadedSD.template_data?.regions, // This fallback may be removed in the future. It has been introduced for compatibility reasons.
          ),
          regionsValue: loadedDeliveryNote,
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error loading initial data');
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [
    setLoading,
    setInitialData,
    sdId,
    noTemplateMsg,
    noSupplierMsg,
    model,
    setError,
    deliveryFieldGroups,
  ]);

  useLayoutEffect(() => {
    if (!concreteTypes.includes(model)) {
      navigate('/');
    }
  }, [model, navigate]);

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.edit.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.edit.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() => {
          if (validated === 'noValidated') {
            navigate(`../noValidated`);
            return;
          }
          navigate(`../validated`);
        }}
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          // Actualizar también el documento con las nuevas regiones
          await updateScannedDocument(
            sdId,
            {
              delivery_note_data: savedFormState,
              regions: diffTemplate,
              fileName: savedFileName,
              camFileName: savedFormState.camFileName,
            },
            model,
          );
          setLoading(false);
          if (validated === 'noValidated') {
            navigate(`../noValidated`);
            return;
          }
          navigate(`../validated`, { replace: true });
        }}
      />
    );
  }
  return (
    <ScannedDocumentsLayout
      model={model}
      initialData={initialData}
      topFormFields={
        <DeliveryFormFields
          model={model}
          isAssayedAndLotified={isAssayedAndLotified}
        />
      }
      fieldGroups={deliveryFieldGroups}
      validableFields={validableFields}
      onFinish={updateDeliveryScannedDocument}
      onBack={() => {
        if (validated === 'noValidated') {
          navigate(`../noValidated`);
          return;
        }
        navigate(`../validated`);
      }}
      mode={validated === 'validated' ? 'edit' : 'create'}
      isAssayedAndLotified={isAssayedAndLotified}
      header={
        <LayoutHeader
          template={initialData?.form?.templateName}
          supplier={initialData?.form?.supplier_id}
        />
      }
    />
  );
};

export default UpdateDeliveryScannedDoc;
